import { obligationOwnership } from "../lib/obligationOwnership";
import { ObligationOwnership } from "@components/obligations/ObligationOwnership";
import { useAsyncAction } from "../hooks/useAsyncAction";
import { useEffect, useMemo, useRef, useState } from "react";
import type { OperationalObligation } from "@io-spoke/contracts/obligations";
import { AlertCircle, ExternalLink, LoaderCircle } from "lucide-react";
import { PageShell } from "@components/layout/PageShell";
import { PageHeader } from "@components/layout/PageHeader";
import { SidebarTrigger } from "@components/ui/sidebar";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import { Card, CardContent } from "@components/ui/card";
import { getOperationalObligations } from "../lib/obligations";
import { recordedOwnerSuggestions, type OwnerSuggestion } from "@components/obligations/AccountableOwnerField";
import { ObligationActionSheet } from "@components/obligations/ObligationActionSheet";

type AttentionFilter = "all" | "needs_review" | "approaching" | "overdue" | "blocked" | "completed";
const stateLabel: Record<OperationalObligation["state"], string> = {
  detected: "New", needs_review: "Needs review", active: "Active", approaching: "Approaching",
  overdue: "Overdue", blocked: "Blocked", completed: "Completed", dismissed: "Dismissed", unverifiable: "Unverifiable",
};

function dateLabel(value: string | null) {
  if (!value) return "No deadline reported";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(new Date(value));
}

function nextAction(obligation: OperationalObligation) {
  if (obligation.state === "completed") return "Source evidence verifies completion.";
  if (obligation.state === "needs_review") return "Review the proposed classification and impact.";
  if (!obligationOwnership(obligation).recorded) return "Record who is responsible in IO Spoke.";
  if (obligation.state === "blocked") return "Remove the blocker or record an escalation.";
  if (obligation.deadlineAt) return `Act before ${dateLabel(obligation.deadlineAt)}.`;
  return "Monitor the connected source for material changes.";
}

function ObligationCard({ obligation, onUpdated, ownerSuggestions }: { obligation: OperationalObligation; onUpdated: () => void | Promise<void>; ownerSuggestions: OwnerSuggestion[] }) {
  const urgent = obligation.state === "overdue" || obligation.severity === "critical";
  return <article className="rounded-lg border bg-card p-5 shadow-sm" aria-labelledby={`obligation-${obligation.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Badge variant={urgent ? "destructive" : "outline"}>{stateLabel[obligation.state]}</Badge>
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{obligation.type.replace(/_/g, " ")}</span>
          {obligation.freshness === "stale" ? <Badge variant="secondary">Stale source</Badge> : null}
        </div>
        <h2 id={`obligation-${obligation.id}`} className="text-base font-semibold leading-6">{obligation.title}</h2>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">{obligation.summary}</p>
      </div>
      <div className="text-left text-xs text-muted-foreground sm:text-right">
        <p className="font-medium text-foreground">{dateLabel(obligation.deadlineAt)}</p>
        <p className="mt-1">{obligation.freshness === "current" ? "Evidence current" : "Evidence may be outdated"}</p>
      </div>
    </div>
    <div className="mt-4 grid gap-4 border-t pt-4 md:grid-cols-3">
      <div><p className="text-xs text-muted-foreground">Customer impact</p><p className="mt-1 text-sm font-medium">{obligation.impact}</p></div>
      <ObligationOwnership obligation={obligation} />
      <div><p className="text-xs text-muted-foreground">Recommended next step</p><p className="mt-1 text-sm font-medium">{nextAction(obligation)}</p></div>
    </div>
    <details className="group mt-4 border-t pt-4 text-sm">
      <summary className="min-h-11 cursor-pointer list-none rounded-md py-2 font-medium text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring">
        Evidence and assessment details
      </summary>
      <div className="mt-2 rounded-md bg-muted/40 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-medium text-foreground">Connected evidence · {obligation.source.provider}</p>
          {obligation.source.externalUrl ? <a className="inline-flex min-h-11 items-center gap-2 rounded-md px-2 text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" href={obligation.source.externalUrl} target="_blank" rel="noreferrer">Open source <ExternalLink className="h-4 w-4" aria-hidden="true" /></a> : null}
        </div>
        <p className="mt-2 leading-6 text-muted-foreground">{obligation.source.evidenceExcerpt}</p>
        <p className="mt-3 text-xs text-muted-foreground">Estimated confidence {Math.round(obligation.confidence * 100)}% · Updated {dateLabel(obligation.updatedAt)}</p>
      </div>
    </details>
    {!(["completed", "dismissed"] as string[]).includes(obligation.state) ? <div className="mt-4 flex justify-end border-t pt-4"><ObligationActionSheet obligation={obligation} onUpdated={onUpdated} ownerSuggestions={ownerSuggestions} /></div> : null}
  </article>;
}

export function ObligationsPage({ navigate }: { navigate?: (path: string) => void }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof getOperationalObligations>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<AttentionFilter>("all");
  const [visibleCount, setVisibleCount] = useState(20);
  const loadAction = useAsyncAction();
  const currentRead = useRef<Promise<void> | null>(null);
  const load = () => {
    const operation = loadAction.run(async () => {
      try {
        setData(await getOperationalObligations());
        setError(null);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "Unable to load obligations.");
      }
    });
    currentRead.current = operation;
    return operation;
  };
  const refreshAfterUpdate = async () => {
    // A read started before this mutation cannot verify its resulting state.
    if (currentRead.current) await currentRead.current;
    await load();
  };
  useEffect(() => { void load(); }, []);
  const ownerSuggestions = useMemo(() => recordedOwnerSuggestions(data?.obligations ?? []), [data]);
  const visible = useMemo(() => data?.obligations.filter((item) => filter === "all" || item.state === filter) ?? [], [data, filter]);
  const filters: Array<{ key: AttentionFilter; label: string; count: number }> = [
    { key: "all", label: "All", count: data?.summary.total ?? 0 },
    { key: "needs_review", label: "Needs review", count: data?.summary.needsReview ?? 0 },
    { key: "approaching", label: "Approaching", count: data?.summary.approaching ?? 0 },
    { key: "overdue", label: "Overdue", count: data?.summary.overdue ?? 0 },
    { key: "blocked", label: "Blocked", count: data?.summary.blocked ?? 0 },
    { key: "completed", label: "Verified", count: data?.summary.completed ?? 0 },
  ];
  return <PageShell maxWidth="6xl">
    <PageHeader leading={<SidebarTrigger />} eyebrow="Operational intelligence" title="Obligations" description="Customer-impacting commitments, deadlines, renewals, and remediations found across connected systems." />
    {!data && !error ? <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground" role="status"><LoaderCircle className="h-5 w-5 animate-spin" />Analyzing connected evidence…</div> : null}
    {error ? <Card className="mt-8 border-destructive/40"><CardContent className="pt-5"><p role="alert" className="text-sm text-destructive">{error}</p><Button className="mt-4" variant="outline" onClick={() => void load()} loading={loadAction.pending} loadingLabel="Loading obligations…">Try again</Button></CardContent></Card> : null}
    {data ? <>
      <div className="mt-8 flex flex-wrap gap-2" role="group" aria-label="Filter obligations">
        {filters.map((item) => <Button key={item.key} size="sm" variant={filter === item.key ? "default" : "outline"} aria-pressed={filter === item.key} onClick={() => { setFilter(item.key); setVisibleCount(20); }}>{item.label} <span className="ml-1 opacity-70">{item.count}</span></Button>)}
      </div>
      <section className="mt-4 space-y-3" aria-live="polite">
        {visible.slice(0, visibleCount).map((item) => <ObligationCard key={item.id} obligation={item} onUpdated={refreshAfterUpdate} ownerSuggestions={ownerSuggestions} />)}
        {visible.length > visibleCount ? <div className="pt-2 text-center"><Button variant="outline" onClick={() => setVisibleCount((count) => count + 20)}>Show more ({visible.length - visibleCount} remaining)</Button></div> : null}
        {visible.length === 0 ? <div className="rounded-lg border border-dashed px-6 py-14 text-center"><AlertCircle className="mx-auto h-6 w-6 text-muted-foreground" /><h2 className="mt-3 font-semibold">{filter === "all" ? "No obligations detected yet" : `No ${filters.find((item) => item.key === filter)?.label.toLowerCase()} obligations`}</h2><p className="mt-1 text-sm text-muted-foreground">{filter === "all" ? "Connect a source or analyze evidence to let IO Spoke surface customer-impacting work." : "Choose another view to see the obligations that currently need attention."}</p>{filter === "all" && navigate ? <Button className="mt-4" onClick={() => navigate("/settings/integrations")}>Connect a source</Button> : null}</div> : null}
      </section>
    </> : null}
  </PageShell>;
}
